import { Controller } from "@hotwired/stimulus"
import { readFormParams, dataJSON, nextLabelSpan, buildPriceFormatting, formattedValue, renderPrice, renderTotalPrice } from "./shared"

// Term-based pricing engine: price/term selection, optional price items,
// accommodation totals and grand totals. Only active for forms with
// `uses_terms: true` (NormalRegistrationForm). Listens for
// `registration:refresh` (dispatched by the submit and promo-code
// controllers) and `registration:accommodationTotalChanged` (dispatched by
// each accommodation row) to recompute pricing after external changes.
export default class extends Controller {
  connect() {
    this.formParams = readFormParams(this.element)
    if (!this.formParams.uses_terms) return

    this.fmt = buildPriceFormatting(this.formParams)

    this._onChange = this._onChange.bind(this)
    this._onRefresh = this._onRefresh.bind(this)
    this._onAccommodationTotalChanged = this._onAccommodationTotalChanged.bind(this)

    this.element.addEventListener("change", this._onChange)
    this.element.addEventListener("registration:refresh", this._onRefresh)
    this.element.addEventListener("registration:accommodationTotalChanged", this._onAccommodationTotalChanged)

    this._movePaymentInfo()

    const termSelects = this.element.querySelectorAll(".price-term-select")
    if (termSelects.length === 0) {
      this.toggleTerm(this.formParams.first_term)
    } else {
      const initial = this.element.querySelector(`.price-term-select[value="${this.formParams.first_term}"]`)
      if (initial) {
        initial.checked = true
        this.toggleTerm(initial.value)
      }
    }
    this.updateAll()
  }

  disconnect() {
    this.element.removeEventListener("change", this._onChange)
    this.element.removeEventListener("registration:refresh", this._onRefresh)
    this.element.removeEventListener("registration:accommodationTotalChanged", this._onAccommodationTotalChanged)
  }

  _onChange(event) {
    const target = event.target
    if (target.matches(".price-term-select")) {
      this.toggleTerm(target.value)
      this.updateAll()
    } else if (target.matches(".price-item")) {
      this._togglePriceCount(target)
      this._setTotalPart(target)
    } else if (target.matches(".price-item-select")) {
      this._setTotalSelectPart(target)
    } else {
      // `.count-sel`/`data-price-item-count` live on the wrapper div rendered
      // around the count <select> (wrapper_html), not on the select itself,
      // so this needs an ancestor lookup rather than a direct match.
      const countWrapper = target.closest(".count-sel")
      if (countWrapper) {
        const priceItem = this.element.querySelector(`[data-price-item="${countWrapper.dataset.priceItemCount}"]`)
        if (priceItem) this._setTotalPart(priceItem)
      }
    }
  }

  _onRefresh() {
    const checked = this.element.querySelector(".price-term-select:checked")
    this.toggleTerm(checked ? checked.value : this.formParams.first_term)
    this.updateAll()
  }

  _onAccommodationTotalChanged() {
    this._recountTotals("accommodation")
  }

  toggleTerm(termIndex) {
    const termHidden = this.element.querySelector("#user_registration_term_id")
    const promoGroupHidden = this.element.querySelector("#user_registration_promo_group_id")
    const promoGroup = promoGroupHidden ? promoGroupHidden.value : ""
    if (termHidden) termHidden.value = termIndex
    if (promoGroupHidden) promoGroupHidden.value = promoGroup

    this.element.querySelectorAll(".price-item").forEach((el) => this._togglePriceCount(el))

    this.element.querySelectorAll("input.price-item").forEach((el) => {
      const values = promoGroup === "" ? dataJSON(el, "priceValues") : (dataJSON(el, "promoValues") || {})[promoGroup]
      const value = values ? parseFloat(values[termIndex]) || 0 : 0
      this._writePriceSpan(el, value)
    })

    this.element.querySelectorAll("input.price-item-select").forEach((el) => {
      const values = promoGroup === "" ? dataJSON(el, "priceValues") : (dataJSON(el, "promoValues") || {})[promoGroup]
      const termValues = values ? values[termIndex] : null
      const value = termValues ? parseFloat(termValues[el.value]) || 0 : 0
      this._writePriceSpan(el, value)
    })

    this.element.querySelectorAll("input.room-item").forEach((el) => {
      const values = promoGroup === "" ? dataJSON(el, "priceValues") : (dataJSON(el, "promoValues") || {})[promoGroup]
      const termValues = values ? values[termIndex] : null
      const roomValues = termValues ? termValues[el.value] : null
      const fullValue = roomValues ? parseFloat(roomValues.full) || 0 : 0
      const partValue = roomValues ? parseFloat(roomValues.part) || 0 : 0
      const span = nextLabelSpan(el)
      if (span) {
        span.innerHTML = renderPrice(this.fmt, fullValue)
        span.dataset.val = fullValue
        span.dataset.partVal = partValue
      }
    })

    this.element.querySelectorAll("input.room-item:checked").forEach((el) => {
      el.dispatchEvent(new Event("registration:priceChange", { bubbles: true }))
    })
  }

  updateAll() {
    this.element.querySelectorAll(".price-item").forEach((el) => this._setTotalPart(el))
    this.element.querySelectorAll(".price-item-select").forEach((el) => this._setTotalSelectPart(el))
  }

  _writePriceSpan(el, value) {
    const span = nextLabelSpan(el)
    if (!span) return
    span.innerHTML = renderPrice(this.fmt, value)
    span.dataset.val = value
  }

  _togglePriceCount(item) {
    const priceItem = item.dataset.priceItem
    const unchecked = !item.checked
    const countBlock = this.element.querySelector(`[data-price-item-count="${priceItem}"]`)
    if (!countBlock) return
    countBlock.classList.toggle("hidden-block", unchecked)
    countBlock.querySelectorAll("input, select").forEach((el) => { el.disabled = unchecked })
  }

  _setTotalPart(item) {
    const span = nextLabelSpan(item)
    const value = span ? parseFloat(span.dataset.val || 0) : 0
    const priceItem = item.dataset.priceItem
    const countBlock = this.element.querySelector(`[data-price-item-count="${priceItem}"]`)
    const countInput = countBlock ? countBlock.querySelector("input:not(:disabled), select:not(:disabled)") : null
    const count = countInput ? parseFloat(countInput.value || 0) : 0
    const total = count * value
    const totalInput = this.element.querySelector(`[data-price-total="${priceItem}"].total-part`)
    if (totalInput) totalInput.value = total
    this._recountTotals(item.closest(".card-body"))
  }

  _setTotalSelectPart(item) {
    let sum = 0
    const group = item.closest(".row")
    if (group) {
      group.querySelectorAll("div.form-check input").forEach((el) => {
        if (el.checked) {
          const span = nextLabelSpan(el)
          sum += parseFloat(span?.dataset.val || 0)
        }
      })
    }
    const priceItem = item.dataset.priceItem
    const totalInput = this.element.querySelector(`[data-price-total="${priceItem}"].total-part`)
    if (totalInput) totalInput.value = sum
    this._recountTotals(item.closest(".card-body"))
  }

  _recountTotals(card) {
    let sum = 0
    let cardEl

    if (card === "accommodation") {
      cardEl = this.element.querySelector(".nested-body")?.closest(".card")
      this.element.querySelectorAll("input.nights-item").forEach((el) => {
        sum += parseFloat(el.dataset.hotelPrice || 0)
      })
    } else {
      cardEl = card
      if (!cardEl) return
      cardEl.querySelectorAll(".total-part").forEach((el) => { sum += parseFloat(el.value || 0) })
    }
    if (!cardEl) return

    cardEl.querySelectorAll(".card-help-total").forEach((el) => { el.innerHTML = sum })
    cardEl.querySelectorAll(".price-summary").forEach((el) => { el.innerHTML = renderTotalPrice(this.fmt, sum) })

    sum = 0
    const formEl = cardEl.closest("form")
    formEl.querySelectorAll(".card-help-total").forEach((el) => { sum += parseFloat(el.innerHTML || 0) })
    formEl.querySelectorAll(".form-help-total").forEach((el) => { el.innerHTML = formattedValue(sum, this.fmt.locale) })
    formEl.querySelectorAll(".total-price-summary").forEach((el) => { el.innerHTML = renderTotalPrice(this.fmt, sum) })
  }

  _movePaymentInfo() {
    this.element.querySelectorAll(".pay-select-info.hidden").forEach((el) => {
      const attr = el.dataset.attr
      const label = this.element.querySelector(`label[for*="_${attr}"]`)
      if (label) {
        label.insertAdjacentHTML("afterend", el.innerHTML)
        label.classList.remove("hidden")
      }
    })
  }
}
